import type { HttpErrorResponse } from '@angular/common/http';

import { createSelector } from '@ngrx/store';

import {
  createEntityCollectionStore,
  memoizeSelector,
  ngrxEvent,
  pipeState,
} from '@core/ngrx-legacy';

import {
  AssignMatchPayload,
  AssignMatchResponse,
  JobMatchesEntity,
  MatchingGeneration,
} from './job-matching-placements.types';

const {
  selectors: defaultSelectors,
  actions,
  namespace,
  reducer,
} = createEntityCollectionStore<JobMatchesEntity>()('JobMatches', {
  idSelector: entity => entity.jobId,
  customActions: adapter => ({
    fetchOne: (state, payload: { jobId: number }) =>
      adapter.markAsPending(state, { scope: null, entities: [payload.jobId] }),
    generate: (state, payload: { jobId: number }) =>
      adapter.markAsPending(state, { scope: null, entities: [payload.jobId] }),
    generateSuccess: (
      state,
      payload: {
        jobId: number;
        data: { generation: MatchingGeneration };
      },
    ) => {
      const entity: JobMatchesEntity = {
        ...state.entityMap[payload.jobId]!,
        generation: payload.data.generation,
      };
      return pipeState(
        state,
        next => adapter.upsertOne(next, entity),
        next => adapter.markAsLoaded(next, { entities: [payload.jobId], scope: null }),
      );
    },
    generateFailed: ngrxEvent<{ error: HttpErrorResponse }>(),
    dismiss: (state, payload: { jobId: number; matchId: number }) =>
      adapter.markAsPending(state, { scope: null, entities: [payload.jobId] }),
    dismissSuccess: (state, payload: { jobId: number; matchId: number }) => {
      const entity = state.entityMap[payload.jobId];
      if (!entity) return state;
      const updated: JobMatchesEntity = {
        ...entity,
        matches: entity.matches.filter(match => match.id !== payload.matchId),
      };
      return pipeState(
        state,
        next => adapter.upsertOne(next, updated),
        next => adapter.markAsLoaded(next, { entities: [payload.jobId], scope: null }),
      );
    },
    dismissFailed: ngrxEvent<{ jobId: number; error: HttpErrorResponse }>(),
    assign: (state, payload: { jobId: number; matchId: number; payload: AssignMatchPayload }) =>
      adapter.markAsPending(state, { scope: null, entities: [payload.jobId] }),
    assignSuccess: (
      state,
      payload: { jobId: number; matchId: number; response: AssignMatchResponse },
    ) => {
      const entity = state.entityMap[payload.jobId];
      if (!entity) return state;
      const updated: JobMatchesEntity = {
        ...entity,
        matches: entity.matches.filter(match => match.id !== payload.matchId),
      };
      return pipeState(
        state,
        next => adapter.upsertOne(next, updated),
        next => adapter.markAsLoaded(next, { entities: [payload.jobId], scope: null }),
      );
    },
    assignFailed: ngrxEvent<{ jobId: number; error: HttpErrorResponse }>(),
    bulkAssign: (state, payload: { jobId: number }) =>
      adapter.markAsPending(state, { scope: null, entities: [payload.jobId] }),
    bulkAssignSuccess: ngrxEvent<{ jobId: number; data: { uuid: string } }>(),
    bulkAssignFailed: ngrxEvent<{ jobId: number; error: HttpErrorResponse }>(),
  }),
});

const selectors = {
  ...defaultSelectors,
  byJobId: memoizeSelector((id: number) =>
    createSelector(defaultSelectors.byId(id), (state: JobMatchesEntity | null) => state || null),
  ),
  meta: defaultSelectors.meta,
};

const JobMatchingPlacementsActions = {
  fetchOne: actions.fetchOne,
  fetchOneSuccess: actions.fetchOneSuccess,
  fetchOneFailed: actions.fetchOneFailed,
  generate: actions.generate,
  generateSuccess: actions.generateSuccess,
  generateFailed: actions.generateFailed,
  dismiss: actions.dismiss,
  dismissSuccess: actions.dismissSuccess,
  dismissFailed: actions.dismissFailed,
  assign: actions.assign,
  assignSuccess: actions.assignSuccess,
  assignFailed: actions.assignFailed,
  bulkAssign: actions.bulkAssign,
  bulkAssignSuccess: actions.bulkAssignSuccess,
  bulkAssignFailed: actions.bulkAssignFailed,
};

export {
  JobMatchingPlacementsActions,
  selectors as JobMatchingPlacementsSelectors,
  namespace as JobMatchingPlacementsNamespace,
  reducer as JobMatchingPlacementsReducer,
};
